'use client';

import {
  Country,
  CountryLabel,
  setCountryCookie,
  setCurrencyCookie,
  useRegionCurrency,
  getLanguageCookie,
  setLanguageCookie,
  CLASSNAME,
} from '@skin-seoul/service';
import { CountryCurrency } from '@skin-seoul/service';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@skin-seoul/design-system';
import { useRef, useState } from 'react';
import { LucideIcon } from '../LucideIcon';

import { getObjectValues } from '@skin-seoul/utils';
import TranslateLanguageSelect from '../TranslateLanguageSelect/TranslateLanguageSelect';

// The <label> above this trigger has no htmlFor and does not wrap it, so the
// combobox's accessible name is just its current value ("Singapore (SGD)") —
// which changes as the test drives it, and is exactly what the currency-switch
// scenarios need to assert against rather than select by. Options themselves
// stay role-reachable via getByRole('option').
const COUNTRY_SELECT_TEST_ID = 'country-change-country-select';

type CountryChangeModalProps = {
  initialCountry: Country;
  disableReload?: boolean;
  isOpen: boolean;
  onClose: () => void;
};

const SelectedCountryDisplay = ({
  value,
  countryItems,
}: {
  value: string;
  countryItems: Array<{ label: string; value: string }>;
}) => {
  const item = countryItems.find((c) => c.value === value);
  if (!item) {
    return <SelectValue placeholder="Select country" className="text-paragraph-small text-neutral-900" />;
  }
  return (
    <SelectValue placeholder="Select country" className="text-paragraph-small text-neutral-900">
      <div className="flex w-fit gap-1">
        {item.label} <span className="w-fit">({CountryCurrency[item.value as Country]})</span>
      </div>
    </SelectValue>
  );
};

export const CountryChangeModal = ({
  initialCountry,
  isOpen,
  onClose,
  disableReload = false,
}: CountryChangeModalProps) => {
  const [selectedCountry, setSelectedCountry] = useState<Country>(initialCountry);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(getLanguageCookie());
  const shippingCountry = getObjectValues(Country);
  const { setCountry } = useRegionCurrency();
  const countryInputRef = useRef<HTMLButtonElement>(null);
  const countryItems = shippingCountry.map((country) => ({
    label: CountryLabel[country],
    value: country,
  }));
  const sortedCountryItems = countryItems.sort((a, b) => a.label.localeCompare(b.label));

  const isButtonActive = selectedCountry !== initialCountry || selectedLanguage !== getLanguageCookie();

  const handleOnSave = (value: Country) => {
    setCountryCookie(value);
    setCurrencyCookie(CountryCurrency[value]);
    setCountry(value);
    if (selectedLanguage) {
      setLanguageCookie(selectedLanguage);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ss:user-language-change', { detail: selectedLanguage }));
      }
    }
    onClose();
    if (!disableReload) {
      const url = new URL(window.location.href);
      url.searchParams.delete('currency');
      url.searchParams.delete('country');
      url.searchParams.delete('locale');
      window.location.replace(url.toString());
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        showCloseButton={false}
        className="rounded mx-5 max-w-[90%] mobile:max-w-[600px] p-0 overflow-hidden"
      >
        <DialogHeader className="bg-primary-600 flex flex-row pl-4 pr-2 py-2 items-center rounded-t justify-between">
          <DialogTitle className="text-paragraph-medium font-medium text-generic-0">Choose Your Location</DialogTitle>
          <button
            className="flex items-center justify-center w-10 h-10 bg-transparent border-none cursor-pointer rounded-md hover:bg-white/10"
            onClick={onClose}
          >
            <LucideIcon name="X" size={20} className="text-white" />
          </button>
        </DialogHeader>
        <div className="px-4 py-5">
          <div className="flex flex-col gap-3 w-full h-full">
            <div className="flex flex-col gap-1.5">
              <label className={`${CLASSNAME.NOT_TRANSLATE} text-paragraph-small text-neutral-900`}>
                Shipping Country and Currency
              </label>
              <Select
                value={selectedCountry}
                onValueChange={(value) => {
                  setSelectedCountry(value as Country);
                }}
              >
                <SelectTrigger
                  ref={countryInputRef}
                  data-testid={COUNTRY_SELECT_TEST_ID}
                  className={`border border-neutral-900 hover:border-neutral-300 *:data-[slot=select-value]:line-clamp-none ${CLASSNAME.NOT_TRANSLATE}`}
                >
                  <SelectedCountryDisplay value={selectedCountry} countryItems={sortedCountryItems} />
                </SelectTrigger>

                <SelectContent
                  className={`max-h-[30vh] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] ${CLASSNAME.NOT_TRANSLATE}`}
                >
                  {sortedCountryItems.map((country) => (
                    <SelectItem
                      key={country.value}
                      value={country.value}
                      className="w-full data-[state=checked]:bg-neutral-100/60"
                    >
                      <div className="flex w-fit gap-1">
                        {country.label} <span className="w-fit">({CountryCurrency[country.value as Country]})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <TranslateLanguageSelect value={selectedLanguage} onChange={setSelectedLanguage} applyImmediately={false} />
          </div>
        </div>
        <DialogFooter className="flex-row justify-end gap-2 px-4 pb-4">
          <Button
            className="text-neutral-900 border-neutral-200 border text-paragraph-small hover:bg-neutral-100/60"
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            className="bg-neutral-900 text-paragraph-small hover:bg-neutral-800"
            variant="primary"
            size="sm"
            disabled={!isButtonActive}
            onClick={() => handleOnSave(selectedCountry)}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
